import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type SyncRunStatus = 'idle' | 'running' | 'success' | 'interrupted' | 'stopped';
export type SyncTaskStage = 'idle' | 'uploading' | 'saving' | 'cleaning';
export type SyncMode = 'single' | 'all';
export type SyncTaskResultStatus = 'success' | 'failed';

export type SyncPhotoIssueReason =
  | 'LOCAL_RECORD_MISSING'
  | 'FILE_PAYLOAD_MISSING'
  | 'SERVER_RESPONSE_INVALID'
  | 'UPLOAD_NOT_CONFIRMED';

export interface SyncPhotoIssue {
  localId: number;
  originalFilename: string;
  thumbnail?: string;
  entryId: number;
  cloneIndex: number;
  entryPath: string[];
  source: 'FAILURE' | 'PHOTO_CONTROL';
  failureName?: string;
  controlId?: number;
  controlLabel?: string;
  reason: SyncPhotoIssueReason;
}

export interface SyncTaskResult {
  taskId: number;
  status: SyncTaskResultStatus;
  reasonKey?: string;
  reasonParams?: Record<string, any>;
  photoIssues?: SyncPhotoIssue[];
}

export interface SyncProgressState {
  mode: SyncMode;
  status: SyncRunStatus;
  syncedTasks: number;
  failedTasks: number;
  totalTasks: number;
  currentTaskId?: number;
  currentTaskIndex: number;
  currentTaskStage: SyncTaskStage;
  currentItemDone: number;
  currentItemTotal: number;
  currentPhotoIndex: number;
  currentPhotoPercent: number;
  taskResults: SyncTaskResult[];
  failureReasonKey?: string;
  failureReasonParams?: Record<string, any>;
  logs: string[];
}

const initialState: SyncProgressState = {
  mode: 'all',
  status: 'idle',
  syncedTasks: 0,
  failedTasks: 0,
  totalTasks: 0,
  currentTaskIndex: 0,
  currentTaskStage: 'idle',
  currentItemDone: 0,
  currentItemTotal: 0,
  currentPhotoIndex: 0,
  currentPhotoPercent: 0,
  taskResults: [],
  failureReasonKey: undefined,
  failureReasonParams: undefined,
  logs: []
};

@Injectable({
  providedIn: 'root'
})
export class SyncProgressService {
  private readonly stateSubject = new BehaviorSubject<SyncProgressState>(initialState);
  readonly state$ = this.stateSubject.asObservable();
  private stopRequested = false;

  get snapshot(): SyncProgressState {
    return this.stateSubject.value;
  }

  start(totalTasks: number, mode: SyncMode): void {
    this.stopRequested = false;
    this.stateSubject.next({
      mode,
      status: 'running',
      syncedTasks: 0,
      failedTasks: 0,
      totalTasks,
      currentTaskIndex: 0,
      currentTaskStage: 'idle',
      currentItemDone: 0,
      currentItemTotal: 0,
      currentPhotoIndex: 0,
      currentPhotoPercent: 0,
      taskResults: [],
      failureReasonKey: undefined,
      failureReasonParams: undefined,
      logs: []
    });
  }

  requestStop(): void {
    this.stopRequested = true;
    this.log('SYNC_PROGRESS.LOG_STOP_REQUESTED');
  }

  isStopRequested(): boolean {
    return this.stopRequested;
  }

  setCurrentTask(taskId: number, index: number): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      currentTaskId: taskId,
      currentTaskIndex: index,
      currentTaskStage: 'idle',
      currentItemDone: 0,
      currentItemTotal: 0,
      currentPhotoIndex: 0,
      currentPhotoPercent: 0
    });
  }

  setStage(stage: SyncTaskStage): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      currentTaskStage: stage
    });
  }

  setCurrentItemProgress(done: number, total: number): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      currentItemDone: done,
      currentItemTotal: total
    });
  }

  setCurrentPhoto(index: number, percent: number): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      currentPhotoIndex: index,
      currentPhotoPercent: percent
    });
  }

  incrementSyncedTasks(): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      syncedTasks: state.syncedTasks + 1
    });
  }

  incrementFailedTasks(): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      failedTasks: state.failedTasks + 1
    });
  }

  setTaskResult(
    taskId: number,
    status: SyncTaskResultStatus,
    reasonKey?: string,
    reasonParams?: Record<string, any>,
    photoIssues?: SyncPhotoIssue[]
  ): void {
    const state = this.snapshot;
    const withoutCurrent = state.taskResults.filter(result => result.taskId !== taskId);
    this.stateSubject.next({
      ...state,
      taskResults: [
        ...withoutCurrent,
        { taskId, status, reasonKey, reasonParams, photoIssues }
      ]
    });
  }

  finish(status: SyncRunStatus): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      status,
      currentTaskStage: 'idle'
    });
  }

  setFailureReason(key: string, params?: Record<string, any>): void {
    const state = this.snapshot;
    this.stateSubject.next({
      ...state,
      failureReasonKey: key,
      failureReasonParams: params
    });
  }

  log(key: string, params?: Record<string, any>): void {
    const state = this.snapshot;
    const payload = {
      ...(params ?? {}),
      __ts: new Date().toISOString()
    };
    const paramsSuffix = `|${JSON.stringify(payload)}`;
    this.stateSubject.next({
      ...state,
      logs: [...state.logs, `${key}${paramsSuffix}`]
    });
  }
}
